import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import { aosRefresh } from '../../../../../global/js/core/core';

/**
 * Handles the interactive behavior of the product breakdown module.
 * Toggles the visibility of a collapsible table when the header is clicked.
 */
function productBreakdownA1() {
    if ('scrollRestoration' in history) {
        history.scrollRestoration = 'manual';
    }
    // Shows the very top of a page.
    window.scrollTo({ top: 0, behavior: 'instant' });

    // Register GSAP plugins
    gsap.registerPlugin(ScrollTrigger);

    // Calculating sticky header height for better pin section positioning
    const header = document.querySelector('.js-header');
    let topOffset = header ? header.getBoundingClientRect().height : 100;

    /**
     * Initializes the product breakdown table interactions.
     * @param {HTMLElement} block - The container element for the product breakdown module.
     */
    function init(block) {
        const section = block.closest('.o-block');
        const tables = gsap.utils.toArray('.js-table');
        if (!section || !tables.length) {
            return;
        }

        const sectionStyle = window.getComputedStyle(section);
        const sectionPaddingTop = parseFloat(
            sectionStyle.getPropertyValue('padding-top'),
        );
        const sectionPaddingBottom = parseFloat(
            sectionStyle.getPropertyValue('padding-bottom'),
        );
        topOffset -= sectionPaddingTop - 10;

        // Calculating additional offset for the section block to ensure the last panel/table is always in a user's viewport.
        const sectionHeight = section.getBoundingClientRect().height;
        const viewportHeight = window.innerHeight;
        const slowScrollDistance = Math.max(
            0,
            sectionHeight - viewportHeight + 100,
        );

        // Scroll distance for tables/panels.
        const panelScrollDistance = tables.length * 600;

        // Adjust total scroll distance by subtracting the container/section scroll movement.
        // Since we're moving the section up, we need less scroll distance.
        // It's needed to avoid huge between the current and following section.
        const totalScrollDistance = Math.max(
            panelScrollDistance - slowScrollDistance,
            panelScrollDistance * 0.5,
        );

        // True when it's initialized, false by default.
        let isInitialized = false;

        // Manually create pin-spacer element
        const spacer = document.createElement('div');
        spacer.style.height = `${
            panelScrollDistance -
            sectionHeight +
            sectionPaddingBottom +
            sectionPaddingBottom +
            220
        }px`;
        spacer.className = 'js-product-breakdown-spacer';
        section.parentNode.insertBefore(spacer, section.nextSibling);

        // First ScrollTrigger: Slow scroll of the container (section.o-block).
        gsap.to(section, {
            y: -slowScrollDistance,
            ease: 'none',
            scrollTrigger: {
                trigger: section,
                start: `top-=${topOffset} top`,
                end: `+=${totalScrollDistance}`,
                scrub: true,
                pin: true,
                pinSpacing: false,
                markers: false,
                onEnter: () => {
                    if (isInitialized) {
                        return;
                    }
                    tables.forEach((table) => {
                        table.classList.remove('opacity-0');
                    });
                    isInitialized = true;
                },
                onLeave: () => {
                    aosRefresh();
                },
            },
        });

        // Second ScrollTrigger: Panel/tables effect.
        let panelTl = gsap.timeline({
            scrollTrigger: {
                trigger: section,
                start: `top-=${topOffset} top`,
                end: `+=${totalScrollDistance}`,
                scrub: true,
            },
        });

        tables.forEach((table, i) => {
            const startProgress = i / tables.length;
            panelTl.from(
                table,
                {
                    y: window.innerHeight,
                    duration: 1 / tables.length,
                    ease: 'none',
                },
                startProgress,
            );
        });
    }

    // Select all modules and initialize them
    document
        .querySelectorAll(`[data-module='productBreakdownA1']`)
        .forEach(init);
}

export default productBreakdownA1;
